import type { ClubInformation } from 'src/enums/club-information';
import type { Language } from 'src/enums/language';

import { ClubSection } from '../../cms/page-widgets/enabled-pages-widget';
import type { CmsClubFileNode } from '../helpers/cms-file-helper';
import { findCmsFileNodeForClub } from '../helpers/cms-file-helper';
import { getClubDefaultLanguage } from '../helpers/language-helper';

export const enabledPagesQuery = `
  query EnabledPagesQuery {
    pages: allFile(filter: { base: { eq: "pages.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          treatmentsPage {
            enabled
            clubSection
          }
          familyPage {
            enabled
            clubSection
          }
          liveClassesPage {
            enabled
            clubSection
          }
          onDemandPage {
            enabled
            clubSection
          }
          golfPage {
            enabled
            clubSection
          }
          yogaPage {
            enabled
            clubSection
          }
          personalTrainingPage {
            enabled
            clubSection
          }
          familyAndKidsPage {
            enabled
            clubSection
          }
          relaxAndSocialisePage {
            enabled
            clubSection
          }
          timetablePage {
            enabled
            clubSection
          }
          clubFacilitiesPage {
            enabled
            clubSection
          }
          clubFacilities_spaPage {
            enabled
            clubSection
          }
          clubFacilities_poolsPage {
            enabled
            clubSection
          }
          clubFacilities_tennisPage {
            enabled
            clubSection
          }
          clubFacilities_padelPage {
            enabled
            clubSection
          }
          clubFacilities_gymPage {
            enabled
            clubSection
          }
          clubFacilities_exerciseClassesPage {
            enabled
            clubSection
          }
          clubFacilities_crechePage {
            enabled
            clubSection
          }
          clubFacilities_kidsClassesPage {
            enabled
            clubSection
          }
        }
      }
    }
  }
`;

export const newEnabledPagesQuery = `
  query EnabledPagesQuery {
    newEnabledPages: allFile(filter: { base: { eq: "new-features.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          timetable
        }
      }
    }
  }
`;

export type EnabledPage = {
  enabled: boolean;
  clubSection: ClubSection;
};

export type EnabledPages = Record<string, EnabledPage>;

export type EnabledPagesPerClub = CmsClubFileNode<EnabledPages>;

export type NewEnabledPages = { timetable: boolean };

export type NewEnabledPagesPerClub = CmsClubFileNode<NewEnabledPages>;

export const getEnabledPagesForClub = (
  enabledPagesPerClub: EnabledPagesPerClub[],
  clubInformation: ClubInformation
): EnabledPages =>
  findEnabledPagesForClub(
    enabledPagesPerClub,
    clubInformation.siteId,
    getClubDefaultLanguage(clubInformation.siteId)
  );

export const getNewEnabledPagesForClub = (
  enabledPagesPerClub: NewEnabledPagesPerClub[],
  clubInformation: ClubInformation
): NewEnabledPages =>
  findCmsFileNodeForClub(
    enabledPagesPerClub,
    clubInformation.siteId,
    getClubDefaultLanguage(clubInformation.siteId)
  )!.childClubJson;

export const isInClubFacilitiesSection = (page: EnabledPage): boolean =>
  page.clubSection === ClubSection.CLUB_FACILITIES;

export const defaultEnabledPage: EnabledPage = {
  enabled: true,
  clubSection: ClubSection.ROOT,
};

const findEnabledPagesForClub = (
  enabledPagesPerClub: EnabledPagesPerClub[],
  clubId: string,
  language: Language
): EnabledPages => {
  const enabledPages = findCmsFileNodeForClub(enabledPagesPerClub, clubId, language)!.childClubJson;
  addLandingPageToEnabledPages(enabledPages);
  return enabledPages;
};

const addLandingPageToEnabledPages = (enabledPages: EnabledPages) => {
  enabledPages.landingPage = {
    enabled: true,
    clubSection: ClubSection.ROOT,
  };
};
